import axios from 'axios';
import { RunpodService } from './RunpodService';

export interface UpscaleResult {
  jobId: string;
  status: 'queued' | 'processing' | 'done' | 'error';
}

export interface JobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'done' | 'error';
  error?: string;
}

export interface UpscaledImageResult {
  jobId: string;
  imageUrl: string;
}

export class UpscaleService extends RunpodService {
  constructor() {
    super('upscaler');
  }

  /**
   * Get pod URL for communication with backend
   * @param port Backend port (default: 8000)
   * @returns Full URL to pod backend
   */
  private getPodUrl(port: number = 8000): string {
    if (!this.podStatus.value) {
      throw new Error('Pod not available');
    }

    const podId = this.podStatus.value.id;
    return `https://${podId}-${port}.proxy.runpod.net`;
  }

  /**
   * Upscale an image via the pod's Python backend (async job)
   * @param file Image file to upscale
   * @param scale Upscaling factor (default: 4)
   * @param model Model to use (default: 'real-esrgan-4x')
   * @param enhanceGfpgan Whether to enhance faces with GFPGAN
   * @param enhanceCodeformer Whether to enhance skin with CodeFormer
   * @returns Job ID and status
   * @throws Error if pod not running or upscale fails
   */
  async upscale(file: File, scale: number = 4, model: string = 'real-esrgan-4x', enhanceGfpgan: boolean = false, enhanceCodeformer: boolean = false): Promise<UpscaleResult> {
    try {
      // Stelle sicher, dass Pod bereit ist
      if (!this.podStatus.value) {
        try {
          await this.ensurePodReady();
        } catch (waitError) {
          console.error('[UpscaleService] ❌ Pod failed to start:', waitError);
          throw new Error('Pod konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.');
        }
      }

      if (!this.podStatus.value) {
        throw new Error('Pod not available');
      }

      const podUrl = this.getPodUrl();
      console.log(`[UpscaleService] 📤 Uploading image to ${podUrl}/upscale...`);

      // Prepare form data for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('model', model);
      formData.append('scale', scale.toString());
      formData.append('enhanceGfpgan', enhanceGfpgan ? 'true' : 'false');
      formData.append('enhanceCodeformer', enhanceCodeformer ? 'true' : 'false');

      // Send request with axios - returns immediately with job_id
      const response = await axios.post<{ job_id: string; status: string }>(`${podUrl}/upscale`, formData);

      console.log('[UpscaleService] ✅ Upscale request queued:', response.data);

      return {
        jobId: response.data.job_id,
        status: (response.data.status as any) || 'queued',
      };
    } catch (error) {
      console.error('[UpscaleService] ❌ Upscale failed:', error);
      throw error;
    }
  }

  /**
   * Get job status from backend
   * @param jobId Job ID to check
   * @returns Job status information
   * @throws Error if job not found
   */
  async getJobStatus(jobId: string): Promise<JobStatus> {
    try {
      const podUrl = this.getPodUrl();
      console.log(`[UpscaleService] 🔍 Checking job status: ${jobId}...`);

      const response = await axios.get<{
        job_id: string;
        status: string;
        error?: string;
      }>(`${podUrl}/job/${jobId}`);

      const jobStatus: JobStatus = {
        jobId: response.data.job_id,
        status: (response.data.status as any) || 'queued',
      };

      if (response.data.error) {
        jobStatus.error = response.data.error;
      }

      console.log(`[UpscaleService] Job status: ${jobStatus.status}`);
      return jobStatus;
    } catch (error) {
      console.error('[UpscaleService] ❌ Failed to get job status:', error);
      throw error;
    }
  }

  /**
   * Wait for job to complete
   * @param jobId Job ID to wait for
   * @param maxWaitSeconds Maximum time to wait in seconds (default: 300)
   * @throws Error if timeout or job fails
   */
  async waitForJobDone(jobId: string, maxWaitSeconds: number = 300): Promise<JobStatus> {
    const pollInterval = 2000; // 2 seconds
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitSeconds * 1000) {
      try {
        const status = await this.getJobStatus(jobId);

        if (status.status === 'done') {
          console.log('[UpscaleService] ✅ Job completed successfully');
          return status;
        }

        if (status.status === 'error') {
          throw new Error(`Job failed: ${status.error || 'Unknown error'}`);
        }

        const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);
        const progress = Math.round((elapsedSeconds / maxWaitSeconds) * 100);
        console.log(`[UpscaleService] ⏳ Waiting for job (${progress}%)... status: ${status.status}`);

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      } catch (error) {
        console.error('[UpscaleService] Error waiting for job:', error);
        throw error;
      }
    }

    throw new Error(`Job did not complete within ${maxWaitSeconds} seconds`);
  }

  /**
   * Retrieve upscaled image from backend
   * @param jobId Job ID from upscale request
   * @returns Upscaled image as Object URL
   * @throws Error if result not found or download fails
   */
  async getResult(jobId: string): Promise<UpscaledImageResult> {
    try {
      const podUrl = this.getPodUrl();
      console.log(`[UpscaleService] 📥 Downloading result for job ${jobId}...`);

      // Send GET request to download the result image
      const response = await axios.get<Blob>(`${podUrl}/result/${jobId}`, {
        responseType: 'blob', // Response is binary image data
      });

      // Create Object URL from Blob
      const imageUrl = URL.createObjectURL(response.data);

      console.log('[UpscaleService] ✅ Result downloaded successfully');

      return {
        jobId: jobId,
        imageUrl: imageUrl,
      };
    } catch (error) {
      console.error('[UpscaleService] ❌ Failed to get result:', error);
      throw error;
    }
  }

  /**
   * Complete upscaling workflow: upload, wait, and download
   * @param file Image file to upscale
   * @param scale Upscaling factor
   * @param enhanceFace Whether to enhance faces
   * @param maxWaitSeconds Maximum time to wait for completion
   * @returns Upscaled image URL
   */
  async upscaleAndWait(file: File, scale: number = 2, enhanceFace: boolean = false, maxWaitSeconds: number = 300): Promise<UpscaledImageResult> {
    try {
      // Step 1: Upload image and get job ID
      console.log('[UpscaleService] 🚀 Starting upscale workflow...');
      const uploadResult = await this.upscale(file, scale, enhanceFace);

      // Step 2: Wait for job to complete
      console.log(`[UpscaleService] ⏳ Waiting for job ${uploadResult.jobId} to complete...`);
      await this.waitForJobDone(uploadResult.jobId, maxWaitSeconds);

      // Step 3: Download the result
      console.log('[UpscaleService] 📥 Downloading upscaled image...');
      const result = await this.getResult(uploadResult.jobId);

      console.log('[UpscaleService] ✨ Upscale workflow completed successfully');
      return result;
    } catch (error) {
      console.error('[UpscaleService] ❌ Upscale workflow failed:', error);
      throw error;
    }
  }
}

export const upscalerPod = new UpscaleService();
